import i18n from '@/i18n'
import {
	ConditionallyImplemented,
	Implemented,
	NeedsAttention,
	NotImplemented,
	PartiallyImplemented,
	RT_Unscored,
	WillNotImplement,
	type MethodStatus,
} from '@/models/enums-generated'
import { riskConfig } from './riskConfig'
import type {
	Asset,
	MitigationMeasure,
	MitigationMeasureSummary,
	ModuleStatuses,
	Risk,
	RiskCatalogMeta,
	RiskConfig,
	RiskMeta,
	StandardMethod,
	StandardModule,
} from '@/models/api-generated'
import { defaultInstance, viewing } from '@/services/utils'

export const formatPercentage = (percentage: number | null | undefined) => {
	return percentage == null ? '-' : `${percentage.toFixed(1)}%`
}

export const formatPrice = (price?: number) => {
	return price == null ? '-' : `${price}€`
}

export const notApplicableGroup = 'NotApplicableGroup'

export const statusColumns = [
	{ key: Implemented, label: i18n.global.t('status.implemented'), color: 'pie1' },
	{ key: PartiallyImplemented, label: i18n.global.t('status.partiallyImplemented'), color: 'pie2' },
	{ key: NotImplemented, label: i18n.global.t('status.notImplemented'), color: 'pie3' },
	{ key: notApplicableGroup, label: `${i18n.global.t('status.willNotApply')}/${i18n.global.t('status.willNotImplement')}`, color: 'pie4' },
	{ key: NeedsAttention, label: i18n.global.t('status.needsAttention'), color: 'pie6' },
]

export const formatAxisLevel = (frac: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const max = config.matrixSize
	return Math.floor(frac * max)
}

export const parseAxisLevel = (int: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const max = config.matrixSize
	return int / max
}

export const formatRiskScore = (frac: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const max = config.matrixSize * config.matrixSize - 1
	return frac * max + 1
}

export const formatRiskScoreStr = (frac: number, fractionDigits?: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const score = formatRiskScore(frac, config)
	return score.toFixed(fractionDigits)
}

export const parseRiskScore = (int: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const max = config.matrixSize * config.matrixSize - 1
	return (int - 1) / max
}

export const scoreToLevel = (riskScore: number, config?: RiskConfig) => {
	if (!config) config = riskConfig.value
	const score = Math.round(formatRiskScore(riskScore, config))

	for (const lvl of config.riskLevels) {
		// rangeMax is persisted as float32, so its fraction never matches an exact score
		// fraction — compare whole scores, the same numbers the level ranges display.
		if (score <= Math.round(formatRiskScore(lvl.rangeMax, config))) return lvl
	}

	return config.riskLevels[config.riskLevels.length - 1]
}

export const calcRiskScore = (probability: number, impact: number) => {
	const a = formatAxisLevel(probability) + 1
	const b = formatAxisLevel(impact) + 1
	return parseRiskScore(a * b)
}

export function defaultRisk(): Risk {
	return {
		id: 0,
		code: '',
		name: '',
		isCustom: true,
		description: '',
		isExpanded: true,
		riskMetas: [defaultRiskMeta()],
		totalPages: 0,
		totalConnectionRows: 0,
		totalRows: 0,
		catalog: { id: 0, name: '', isCustom: false },
	}
}

export function defaultRiskMeta(asset?: Asset, riskMeta?: RiskMeta): RiskMeta {
	return {
		id: 0,
		isCustom: true,
		probability: undefined,
		impact: undefined,
		score: 0,
		residual: 0,
		responsible: undefined,
		confidentiality: true,
		integrity: true,
		availability: true,
		treatment: RT_Unscored,
		dueDate: undefined,
		history: undefined,
		vulnerabilities: riskMeta?.vulnerabilities ?? [],
		threats: riskMeta?.threats ?? [],
		comment: '',
		urlReferences: [],
		documentReferences: [],
		events: [],
		acceptedResidualRisk: undefined,
		decisions: [],
		instance: riskMeta?.instance ?? defaultInstance(),
		impactedAssets: [],
		catalogMeta: riskMeta?.catalogMeta ?? newDefaultCatalog(),
		category: riskMeta?.category ?? {
			id: 0,
			metaId: 0,
			name: '',
			isCustom: false,
			instanceId: 0,
		},
		mitigationSummary: [defaultMitigationSummary()],
		risk_id: riskMeta?.risk_id ?? 0,
		isExpanded: true,
		asset: asset ?? {
			id: 0,
			inventoryNr: '',
			name: '',
			instance: defaultInstance(),
			location: '',
			price: 0,
			comment: '',
			status: false,
			isConnected: false,
			customFields: {},
			isChecked: false,
			isExpanded: false,
			urlReferences: [],
			documentReferences: [],
			subAssets: [],
			risks: [],
		},
	}
}

export function newDefaultCatalog(): RiskCatalogMeta {
	return {
		id: 0,
		instanceId: viewing.value,
		numRisks: 0,
		numThreats: 0,
		numVulnerabilities: 0,
		catalog: {
			id: 0,
			name: '',
			isCustom: true,
		},
	}
}

export function emptyStatuses(): ModuleStatuses {
	return {
		i: { count: 0, percentage: 0 },
		ci: { count: 0, percentage: 0 },
		pi: { count: 0, percentage: 0 },
		ni: { count: 0, percentage: 0 },
		na: { count: 0, percentage: 0 },
		wna: { count: 0, percentage: 0 },
		wni: { count: 0, percentage: 0 },
	}
}

export function defaultMitigationSummary(
	instanceId?: number,
	riskMetaId?: number,
	module?: StandardModule,
	measures?: StandardMethod[],
): MitigationMeasureSummary {
	let mitigationMeasures: MitigationMeasure[] | undefined
	if (module && measures) {
		mitigationMeasures = defaultMitigationMeasures(module.id, module.name, measures, instanceId, riskMetaId)
	}

	return {
		moduleName: module?.name ?? 'total',
		isExpanded: true,
		applicableCount: 0,
		deletedIds: [],
		moduleStatuses: emptyStatuses(),
		mitigationMeasures: mitigationMeasures ?? [],
		moduleId: module?.id ?? 0,
		price: 0,
	}
}

export function defaultMitigationMeasures(
	moduleId: number,
	moduleName: string,
	measures: StandardMethod[],
	instanceId?: number,
	riskMetaId?: number,
): MitigationMeasure[] {
	const mitigationMeasures = measures.map((m) => ({
		id: 0,
		methodId: m.id,
		instanceId: instanceId ?? 0,
		riskMetaId: riskMetaId ?? 0,
		method: m,
		status: undefined,
		assetId: 0,
		assetName: '',
		price: undefined,
		priority: undefined,
		dueDate: undefined,
		responsible: undefined,
		moduleId: moduleId,
		moduleName: moduleName,
		isCustom: true,
		isApplicable: true,
	}))

	return mitigationMeasures
}

export const weight: Record<MethodStatus, number> = {
	[NotImplemented]: 0.0,
	[PartiallyImplemented]: 0.5,
	[Implemented]: 1.0,
	[ConditionallyImplemented]: 1.0,
	[NeedsAttention]: 0.0,
	[WillNotImplement]: 0.0,
}
